import { ComponentType, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { isEmptyOrNull } from '@/utils'
import {
  ContentComponentProps,
  ContentProps,
  CountryCode,
  LocalityType
} from '@/features/Listings/seo/types'

import HeadContent from './head'

const getCityContentComponent = (
  citySlug?: string,
  countryCode?: CountryCode
) => {
  const country = countryCode === 'US' ? 'usa' : 'canada'
  const cityPath = `./city/custom/${citySlug}`
  const genericFallbackPath = `./city/generic/${country}`
  const CityComponent: ComponentType<ContentComponentProps> = dynamic(() =>
    import(`${cityPath}`).catch(() => {
      return dynamic(() => import(`${genericFallbackPath}`))
    })
  )
  return CityComponent
}

const getLocalityContentComponent = (
  neighbourhoodSlug: string,
  citySlug: string,
  type: LocalityType,
  category: string,
  state: string
) => {
  const neighbourhoodKey = `${neighbourhoodSlug}-${citySlug}`
  const neighbourhoodPath = `./${type}/custom/${neighbourhoodKey}`
  const genericFallbackPath = `./${type}/generic`
  if (type === 'poi') {
    const stateSlug = state.toLowerCase()
    if (category === 'transit') {
      // Transit POIs use mix of state and city specific templates
      const PoiComponent: ComponentType<ContentComponentProps> = dynamic(() =>
        import(`./${type}/${category}/${stateSlug}/${citySlug}`).catch(() => {
          return dynamic(() => import(`./${type}/${category}/${stateSlug}`))
        })
      )
      return PoiComponent
    }
    // Other POIs use generic template regardless of state/city
    return dynamic(() => import(`./${type}/${category}`))
  }
  const NeighbourhoodComponent: ComponentType<ContentComponentProps> = dynamic(
    () =>
      import(`${neighbourhoodPath}`).catch(() => {
        return dynamic(() => import(`${genericFallbackPath}`))
      })
  )
  return NeighbourhoodComponent
}

const Content = ({
  cityData,
  neighborhoodData,
  metadata,
  filterSlug
}: ContentProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isChinese = router.locale === 'zh'
  const isCanadaPage = isEmptyOrNull(cityData)
  const cityName = isCanadaPage ? t('seo:Canada') : cityData?.translated
  const nameWithState = isChinese
    ? `${cityData?.translatedState}${cityData?.translated}`
    : `${cityData?.translated}, ${cityData?.translatedState}`
  const cityNameWithState = isCanadaPage ? t('seo:Canada') : nameWithState

  const isNeighborhoodPage = neighborhoodData?.type === 'hood'
  const isUniversityPage = neighborhoodData?.type === 'university'
  const isPoiPage = neighborhoodData?.type === 'poi'
  const isLocalityPage = isNeighborhoodPage || isUniversityPage || isPoiPage

  const ContentComponent = useMemo(
    () =>
      isLocalityPage
        ? getLocalityContentComponent(
            neighborhoodData.slug?.slug_name,
            neighborhoodData.slug?.slug_city,
            neighborhoodData?.type,
            neighborhoodData?.location_info?.category,
            neighborhoodData?.state
          )
        : getCityContentComponent(cityData?.slug, cityData?.country_code),
    [
      cityData?.slug,
      cityData?.country_code,
      neighborhoodData?.type,
      neighborhoodData?.slug,
      neighborhoodData?.location_info,
      neighborhoodData?.state,
      isLocalityPage
    ]
  )

  return (
    <>
      <HeadContent
        metadata={metadata}
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        cityData={cityData}
        isNeighborhoodPage={isNeighborhoodPage}
        isUniversityPage={isUniversityPage}
        isPoiPage={isPoiPage}
        neighborhoodData={neighborhoodData}
        isChinese={isChinese}
      />
      <ContentComponent
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        cityData={cityData}
        neighborhoodName={neighborhoodData?.translated}
        neighbourhoodSlug={neighborhoodData?.slug?.slug_name}
        nearby={neighborhoodData?.nearby}
        isChinese={isChinese}
      />
    </>
  )
}

export default Content
